import type { ConnectionMode, ConnectionOverrides } from '@chaos/types/orgs';

import { apiJson } from './client';

export type TransferPolicyValue = 'allow' | 'ask' | 'deny';

export interface WorkspaceTransferPolicy {
  orgId: string;
  skillsMethods: TransferPolicyValue;
  roleConfigs: TransferPolicyValue;
  factsData: 'deny';
  credentials: 'deny';
  updatedAt: string | null;
  updatedBy: string | null;
  isDefault: boolean;
}

export interface TransferRequest {
  id: string;
  assetKind: string;
  assetId: string;
  assetRef: unknown;
  assetClass: string;
  status: string;
  policyAtRequest: string;
  requestedBy: string;
  requestedByName: string | null;
  toAccountId: string | null;
  toOrgId: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
  expiresAt: string;
  consumedAt: string | null;
  consumedBy: string | null;
  consumptionRef: unknown;
}

export type EngagementKind = 'member' | 'external' | 'supervisory';

export interface EngagementCeiling {
  maxOrgRole: 'founder' | 'admin' | 'member' | 'viewer';
  allowsSharedMode: boolean;
  transfer: 'inherit' | 'tighten' | 'deny';
  summary: string;
}

export interface ConnectionProfileView {
  id: string | null;
  orgId: string;
  userId: string;
  accountId?: string | null;
  mode: ConnectionMode;
  engagement: EngagementKind;
  overrides: ConnectionOverrides;
  status: string;
  consentVersion: string | null;
  acceptedAt: string | null;
  source: string | null;
  proposedBy?: string | null;
  supersedesId?: string | null;
  createdAt: string | null;
  ceiling: EngagementCeiling;
  isDefault?: boolean;
}

export interface ActiveConsentDocument {
  version: string;
  bodyMd: string;
  retains: unknown;
  leavesWithYou: unknown;
  modes: unknown;
}

export interface RoleConnectionView {
  id: string;
  orgId: string;
  memberUserId: string;
  roleId: string;
  originOrgId: string;
  originAccountId: string;
  profileId: string;
  mode: ConnectionMode;
  overrides: ConnectionOverrides;
  status: string;
  disconnectReason: string | null;
  connectedAt: string;
  disconnectedAt: string | null;
  roleSlug: string | null;
  roleName: string | null;
  memberName: string | null;
  snapshotCount: number;
  latestSnapshotVersion: number | null;
  latestManifestSha256: string | null;
}

export const knowledgeIpApi = {
  getTransferPolicy(orgId: string): Promise<WorkspaceTransferPolicy> {
    return apiJson<{ policy: WorkspaceTransferPolicy }>(`/orgs/${orgId}/transfer-policy`).then(
      (result) => result.policy,
    );
  },

  updateTransferPolicy(
    orgId: string,
    input: { skillsMethods: TransferPolicyValue; roleConfigs: TransferPolicyValue },
  ): Promise<WorkspaceTransferPolicy> {
    return apiJson<{ policy: WorkspaceTransferPolicy }>(`/orgs/${orgId}/transfer-policy`, {
      method: 'PUT',
      json: input,
    }).then((result) => result.policy);
  },

  listTransferRequests(orgId: string): Promise<TransferRequest[]> {
    return apiJson<{ requests: TransferRequest[] }>(
      `/orgs/${orgId}/transfer-requests?status=pending`,
    ).then((result) => result.requests);
  },

  decideTransferRequest(
    orgId: string,
    requestId: string,
    action: 'approve' | 'deny',
  ): Promise<TransferRequest> {
    return apiJson<{ request: TransferRequest }>(
      `/orgs/${orgId}/transfer-requests/${requestId}/decide`,
      { method: 'POST', json: { action } },
    ).then((result) => result.request);
  },

  listConnectionProfiles(orgId: string): Promise<{
    profiles: ConnectionProfileView[];
    defaults: Pick<ConnectionProfileView, 'mode' | 'engagement' | 'ceiling'>;
    scope: 'all' | 'self';
  }> {
    return apiJson(`/orgs/${orgId}/connection-profiles`);
  },

  getConnectionProfile(
    orgId: string,
    userId: string,
  ): Promise<{ profile: ConnectionProfileView; pendingProposal: ConnectionProfileView | null }> {
    return apiJson(`/orgs/${orgId}/members/${userId}/connection-profile`);
  },

  proposeConnectionProfile(
    orgId: string,
    userId: string,
    input: { mode: ConnectionMode; engagement: EngagementKind; overrides: ConnectionOverrides },
  ): Promise<ConnectionProfileView> {
    return apiJson<{ proposal: ConnectionProfileView }>(
      `/orgs/${orgId}/members/${userId}/connection-profile/proposals`,
      { method: 'POST', json: input },
    ).then((result) => result.proposal);
  },

  decideConnectionProposal(
    orgId: string,
    userId: string,
    proposalId: string,
    action: 'approve' | 'decline',
  ): Promise<ConnectionProfileView> {
    return apiJson<{ profile: ConnectionProfileView }>(
      `/orgs/${orgId}/members/${userId}/connection-profile/proposals/${proposalId}/decide`,
      { method: 'POST', json: { action } },
    ).then((result) => result.profile);
  },

  getActiveConsent(): Promise<ActiveConsentDocument> {
    return apiJson('/consent-documents/active');
  },

  listRoleConnections(orgId: string): Promise<RoleConnectionView[]> {
    return apiJson<{ connections: RoleConnectionView[] }>(`/orgs/${orgId}/role-connections`).then(
      (result) => result.connections,
    );
  },

  disconnectRole(orgId: string, connectionId: string): Promise<void> {
    return apiJson(`/orgs/${orgId}/role-connections/${connectionId}`, {
      method: 'DELETE',
    }).then(() => undefined);
  },
};

export const knowledgeIpKeys = {
  root: ['knowledge-ip'] as const,
  policy: (orgId: string) => [...knowledgeIpKeys.root, orgId, 'policy'] as const,
  requests: (orgId: string) => [...knowledgeIpKeys.root, orgId, 'requests'] as const,
  profiles: (orgId: string) => [...knowledgeIpKeys.root, orgId, 'profiles'] as const,
  profile: (orgId: string, userId: string) =>
    [...knowledgeIpKeys.root, orgId, 'profile', userId] as const,
  consent: () => [...knowledgeIpKeys.root, 'consent'] as const,
  roleConnections: (orgId: string) =>
    [...knowledgeIpKeys.root, orgId, 'role-connections'] as const,
};
